import React, { useEffect, useRef, useState, useCallback } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import HardBreak from '@tiptap/extension-hard-break';

/**
 * RichTextEditor based on Tiptap v2
 * --------------------------------------------------
 * Key goals preserved from the legacy component:
 *  1. Cursor stability during editing and after programmatic inserts.
 *  2. Accepts and renders raw HTML (bold tags, lists, <br>, etc.).
 *  3. Custom toolbar with Diagnosis helpers and template insertion.
 *  4. Public `insertTranscript(text)` that restores the saved selection
 *     (used by your voice‑recorder modal) before inserting text.
 */

const RichTextEditorTiptap = ({
  value = '',            // incoming HTML string
  onChange,              // (html) => void
  readOnly = false,      // Add readOnly prop with default false
}) => {
  /* --------------------------------------------------
   * Selection snapshot for out‑of‑band inserts
   * -------------------------------------------------- */
  const savedSelectionRef = useRef(null);

  /* --------------------------------------------------
   * Editor initialisation
   * -------------------------------------------------- */
  const editor = useEditor({
    editable: !readOnly,  // Set editable based on readOnly prop
    autofocus: true,
    content: value || '',   // initial HTML
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true },
        hardBreak: true, // Enable hard breaks
        paragraph: {
          HTMLAttributes: {
            class: 'my-2', // Add margin to paragraphs
          },
        },
      }),
      Underline,
      HardBreak.configure({
        keepMarks: true,
        HTMLAttributes: {
          class: 'my-1', // Add margin to line breaks
        },
      }),

      /* Table support (Word‑style) */
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,

      /* Optional placeholder – remove if not needed */
      Placeholder.configure({ placeholder: 'Start typing …' }),
    ],

    onUpdate({ editor }) {
      // Process the HTML to ensure consistent line breaks
      const html = editor.getHTML()
        .replace(/<p><br><\/p>/g, '<br>') // Replace empty paragraphs with single breaks
        .replace(/<p>\s*<\/p>/g, '<br>') // Replace empty paragraphs with single breaks
        .replace(/<br\s*\/?>/g, '<br>') // Normalize break tags
        .replace(/\n/g, '<br>'); // Convert newlines to breaks
      
      onChange?.(html);
    },

    // Add keyboard shortcuts configuration here
    editorProps: {
      handleKeyDown: (view, event) => {
        // Handle Tab key
        if (event.key === 'Tab') {
          event.preventDefault();
          editor.commands.insertContent('\u00A0\u00A0\u00A0\u00A0\u00A0');
          return true;
        }
        
        // Handle Ctrl+D for Dx Template
        if (event.ctrlKey && event.key === 'd') {
          event.preventDefault();
          const template = '<li><b>Diagnosis</b><br/>  - <br/>  - <br/>  Plan: </li>';
          editor.commands.insertContent(template);
          return true;
        }

        // Handle Enter key for line breaks
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          editor.commands.insertContent('<br>');
          return true;
        }
        
        return false;
      },
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none whitespace-pre-wrap',
        contenteditable: true,
      },
    },
  });

  /* --------------------------------------------------
   * Keep external `value` prop in sync without breaking undo history.
   * -------------------------------------------------- */
  useEffect(() => {
    if (!editor || !value) return;
    if (value !== editor.getHTML()) {
      // Pre-process the HTML to ensure line breaks are properly handled
      const processedHtml = value
        .replace(/\n/g, '<br>')  // Convert newlines to <br>
        .replace(/\r/g, '')      // Remove carriage returns
        .replace(/<br\s*\/?>/g, '<br>') // Normalize <br> tags
        .replace(/<p><br><\/p>/g, '<br>') // Replace empty paragraphs with single breaks
        .replace(/<p>\s*<\/p>/g, '<br>'); // Replace empty paragraphs with single breaks
      
      editor.commands.setContent(processedHtml, false);
    }
  }, [editor, value]);

  /* --------------------------------------------------
   * Toolbar command helpers
   * -------------------------------------------------- */
  const insertDx = useCallback(() => {
    editor?.chain().focus().insertContent('<li><b>Diagnosis </b>:</li>').run();
  }, [editor]);

  const insertDxNumbered = useCallback(() => {
    editor?.chain().focus().insertContent('<ol><li><b>Diagnosis </b>:</li></ol>').run();
  }, [editor]);

  const insertDxTemplate = useCallback(() => {
    const template = '<li><b>Diagnosis</b><br/>  - <br/>  - <br/>  Plan: </li>';
    editor?.chain().focus().insertContent(template).run();
  }, [editor]);

  /* --------------------------------------------------
   * Context‑menu → save selection, open recorder modal, etc.
   * -------------------------------------------------- */
  const handleContextMenu = (e) => {
    e.preventDefault();
    if (!editor) return;
    savedSelectionRef.current = {
      from: editor.state.selection.from,
      to: editor.state.selection.to,
    };
    // 👉 Open your recorder UI here …
  };

  /* --------------------------------------------------
   * Public helper – call from recorder once transcript is ready.
   * -------------------------------------------------- */
  const insertTranscript = useCallback((text) => {
    if (!editor) return;
    editor.commands.focus();
    const saved = savedSelectionRef.current;
    if (saved) {
      editor.commands.setTextSelection(saved);
    }
    editor.commands.insertContent(text);
  }, [editor]);

  /* --------------------------------------------------
   * Toolbar button component (internal)
   * -------------------------------------------------- */
  const Btn = ({ onClick, title, children }) => (
    <button
      type="button"
      className="toolbar-button"
      title={title}
      onMouseDown={(e) => { e.preventDefault(); onClick?.(); }}
    >
      {children}
    </button>
  );

  if (!editor) return null; // still initialising

  return (
    <div className="rich-text-editor ml-[7px] mt-[15px]">
      {/* -------------------------------------------------- Toolbar */}
      <div className="editor-toolbar">
        <Btn onClick={() => editor.chain().focus().toggleBold().run()}><b>B</b></Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()}><i>I</i></Btn>
        <Btn onClick={() => editor.chain().focus().toggleUnderline().run()}><u>U</u></Btn>
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()}>• List</Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()}>1. List</Btn>
        <Btn onClick={insertDx} title="Insert Diagnosis (Bullet)">• Dx</Btn>
        <Btn onClick={insertDxNumbered} title="Insert Diagnosis (Numbered)">1. Dx</Btn>
        <Btn onClick={insertDxTemplate} title="Insert Diagnosis Template">•Dx – Plan</Btn>
      </div>

      {/* -------------------------------------------------- Editable area */}
      <EditorContent
        editor={editor}
        onContextMenu={handleContextMenu}
        className="editable-content"
        style={{
          border: '1px solid #ccc',
          padding: '10px',
          minHeight: '200px',
          backgroundColor: 'white',
          borderRadius: '4px',
        }}
      />
    </div>
  );
};

export default RichTextEditorTiptap;
